import React from 'react';

import { useSiteConfig } from 'hooks/useSiteConfig';
import {
  PaymentMethod,
  PaymentMethod as PaymentMethodEnum,
} from 'utils/constants';

type CheckoutPaymentFormState = {
  formDisabled: boolean;
  formLoading: boolean;
  submitButtonEnabled: boolean;
  formError: string | undefined;
  currentPaymentMethod: PaymentMethod;
  allowedPaymentMethods: PaymentMethod[];
};

export type CheckoutPaymentContext = {
  /**
   *  Assigns the formHandler to a mutable reference. `formHandler` is a function
   *  that can be called and set in different places.
   *
   *  For example: We can define how we want to handle a submission for Credit
   *  Cards. However, the <Button/> and the <CreditCard /> components are siblings in the
   *  DOM.
   *
   *  We can set the handler function in the body and call it in the button with
   *  the help of this ref.
   */
  formHandler: React.MutableRefObject<CallableFunction | null>;
  /**
   * Use this setter to dim (disable) the Submit button in the form and lock the
   * radio buttons. Useful when
   * we don't want to signal submission (for example, setting the <Button /> to a
   * loading state), but want to disable it because some other operation is in the
   * background.
   *
   * For example: while we cancel an order, we want to disable the <PaymentButton
   * /> but we don't want the loading spinner to show, as that might indicate a
   * payment is happening.
   */
  setFormDisabled: (disabled: boolean) => void;
  /**
   * Use this setter to dim (disable) the Submit button in the form, lock the
   * radio buttons and put a loading indicator to the <Button />. Used mostly
   * for submission.
   * When used with argument `loading: true` it automatically clears the `formError`.
   * */
  setFormLoading: (loading: boolean) => void;
  /**
   * Use this setter to enable the submit button
   * */
  setSubmitButtonEnabled: (enabled: boolean) => void;
  /**
   * Sets the `formError`.
   */
  setFormError: (formError: string | undefined) => void;
  /**
   * Sets the `currentPaymentMethod`. It automatically clears the `formError`.
   */
  setCurrentPaymentMethod: (
    currentPaymentMethod: PaymentMethod,
    submitButtonEnabled?: boolean
  ) => void;
  /**
   * Sets the `setAllowedPaymentMethods`.
   */
  setAllowedPaymentMethods: (allowedPaymentMethods: PaymentMethod[]) => void;
  formState: CheckoutPaymentFormState;
};

type CheckoutPaymentContextProps = { children?: React.ReactNode };

const CheckoutPaymentContext = React.createContext<CheckoutPaymentContext>(
  null as any
);

export const CheckoutPaymentProvider = (props: CheckoutPaymentContextProps) => {
  const { defaultPaymentMethod } = useSiteConfig();

  // --------------------------------------------------------------------
  // Hoisted form states
  // --------------------------------------------------------------------
  //
  // These are states hoisted up, so it can be shared with the <FormButton />.
  // Every payment type has the option to manipulate these as they seem fit.
  //
  // TODO: Think about bringing this inside the <FormBody />. That would allow
  // us to remove the hoisting and the simplify the context.
  const [formState, setFormState] = React.useState<CheckoutPaymentFormState>({
    formDisabled: false,
    formLoading: false,
    submitButtonEnabled: true,
    currentPaymentMethod:
      (defaultPaymentMethod as PaymentMethod) ?? PaymentMethodEnum.CREDIT_CARD,
    allowedPaymentMethods: [],
    formError: undefined,
  });

  const formHandler = React.useRef<CallableFunction | null>(null);

  const setCurrentPaymentMethod = (
    currentPaymentMethod: PaymentMethod,
    submitButtonEnabled = false
  ) => {
    setFormState(prevState => {
      return {
        ...prevState,
        formError: undefined,
        currentPaymentMethod,
        submitButtonEnabled,
      };
    });
  };

  const setAllowedPaymentMethods = (allowedPaymentMethods: PaymentMethod[]) => {
    setFormState(prevState => {
      return {
        ...prevState,
        formError: undefined,
        allowedPaymentMethods,
      };
    });
  };

  const setFormLoading = (loading: boolean) => {
    setFormState(prevState => {
      return {
        ...prevState,
        formDisabled: loading,
        formLoading: loading,
        formError: loading ? undefined : prevState.formError,
      };
    });
  };

  const setFormDisabled = (disabled: boolean) => {
    setFormState(prevState => {
      return {
        ...prevState,
        formDisabled: disabled,
      };
    });
  };

  const setSubmitButtonEnabled = (enabled: boolean) => {
    setFormState(prevState => {
      return {
        ...prevState,
        submitButtonEnabled: enabled,
      };
    });
  };

  const setFormError = (formError: string | undefined) => {
    setFormState(prevState => {
      return {
        ...prevState,
        formError,
      };
    });
  };

  return (
    <CheckoutPaymentContext.Provider
      value={React.useMemo(() => {
        return {
          formHandler,
          formState,
          setFormLoading,
          setFormDisabled,
          setSubmitButtonEnabled,
          setFormError,
          setCurrentPaymentMethod,
          setAllowedPaymentMethods,
        };
      }, [formState])}
    >
      {props.children}
    </CheckoutPaymentContext.Provider>
  );
};

export const useCheckoutPayment = () =>
  React.useContext(CheckoutPaymentContext);
